import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Bell, FileSignature, Gift, Megaphone, ShieldCheck, Wallet } from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import { useNotifications } from '../../contexts/NotificationContext';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../utils/apiService';
import {
  emitNotificationPreferenceChanged,
  isMutedFromPreferences,
  onNotificationPreferenceChanged,
} from '../../utils/notificationPreferences';
import { getThemeColor } from '../../config/appConfig';
import { formatNotificationTime } from '../../utils/notificationData';
import { notificationRoute, notificationTypeLabel } from '../../utils/partnerAgreements';
import '../../styles/module-shared.css';
import '../../components/notifications/notifications.css';

const THEME = getThemeColor('primary');

const typeIcon = (type = '') => {
  const key = String(type).toLowerCase();
  if (key.includes('agreement')) return FileSignature;
  if (key.includes('promo') || key.includes('bonus')) return Gift;
  if (key.includes('withdraw') || key.includes('deposit') || key.includes('payout')) return Wallet;
  if (key.includes('kyc') || key.includes('verif')) return ShieldCheck;
  if (key.includes('reward') || key.includes('loyalty')) return Megaphone;
  return Bell;
};

const TAB_ALL = 'all';
const TAB_PREFERENCES = 'preferences';

/**
 * Notifications → Preferences: single "Mute Notifications" ON/OFF (same switch as the bell drawer "Mute").
 * Saved to AffiliateUsers.ReceiveNotifications (= !muted). Muted only silences toasts/popups; notifications are still created and listed.
 */
const NotificationPreferences = () => {
  const { showSuccess, showError } = useToast();
  // Toast helpers are recreated every render; keep the latest in a ref so loading runs once.
  const toastRef = useRef({ showSuccess, showError });
  toastRef.current = { showSuccess, showError };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMuted, setSavedMuted] = useState(false);
  const [muted, setMuted] = useState(false);

  const loadPreferences = useCallback(async () => {
    setLoading(true);
    const res = await apiService.getAffiliateNotificationPreferences();
    if (res?.success) {
      const next = isMutedFromPreferences(res.data);
      setSavedMuted(next);
      setMuted(next);
    } else {
      toastRef.current.showError(res?.error || 'Failed to load notification preferences');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  // Bell drawer toggled Mute → reflect it here immediately.
  useEffect(
    () =>
      onNotificationPreferenceChanged((next) => {
        setSavedMuted(next);
        setMuted(next);
      }),
    []
  );

  const savePreferences = async () => {
    setSaving(true);
    const res = await apiService.updateAffiliateNotificationPreferences(!muted);
    setSaving(false);
    if (res?.success) {
      setSavedMuted(muted);
      emitNotificationPreferenceChanged(muted);
      toastRef.current.showSuccess('Notification preferences saved successfully.');
    } else {
      toastRef.current.showError(res?.error || 'Failed to save notification preferences');
    }
  };

  if (loading) {
    return (
      <div className="module-card">
        <AppLoader color={THEME} />
      </div>
    );
  }

  const dirty = muted !== savedMuted;

  return (
    <section className="module-card notif-prefs" aria-labelledby="notif-prefs-title">
      <h2 id="notif-prefs-title" className="notif-prefs__title">Notification Preferences</h2>
      <div className="notif-prefs__row">
        <div className="notif-prefs__text">
          <span className="notif-prefs__label" id="notif-prefs-mute-label">Mute Notifications</span>
          <span className="notif-prefs__hint">
            {muted
              ? 'Notifications are muted — pop-up alerts are hidden. Notifications are still saved here.'
              : 'You will see pop-up alerts for new notifications.'}
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={muted}
          aria-labelledby="notif-prefs-mute-label"
          className={`notif-switch${muted ? ' is-on' : ''}`}
          style={{ '--notif-accent': THEME }}
          onClick={() => setMuted((v) => !v)}
          disabled={saving}
        >
          <span className="notif-switch__state">{muted ? 'ON' : 'OFF'}</span>
          <span className="notif-switch__knob" aria-hidden="true" />
        </button>
      </div>
      <div className="notif-prefs__actions">
        <button
          type="button"
          className="notif-prefs__save"
          style={{ background: THEME }}
          onClick={savePreferences}
          disabled={saving || !dirty}
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </section>
  );
};

const NotificationsPage = () => {
  const {
    notifications,
    connectionStatus,
    historyLoading,
    markAllSeen,
    markOneSeen,
    fetchHistory,
  } = useNotifications();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === TAB_PREFERENCES ? TAB_PREFERENCES : TAB_ALL;

  const selectTab = (tab) => {
    if (tab === TAB_PREFERENCES) setSearchParams({ tab: TAB_PREFERENCES });
    else setSearchParams({});
  };

  const openNotification = (item) => {
    markOneSeen(item.identity);
    const route = notificationRoute(item.type);
    if (route) navigate(route);
  };

  useEffect(() => {
    markAllSeen();
    fetchHistory();
  }, [markAllSeen, fetchHistory]);

  return (
    <div className="page notif-page">
      <div className="page-inner">
        <ModulePageHeader
          icon={Bell}
          title="Notifications"
          subtitle={`Live status: ${connectionStatus}`}
          iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
        />

        <div className="notif-tabs" role="tablist" aria-label="Notifications">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === TAB_ALL}
            className={`notif-tabs__tab${activeTab === TAB_ALL ? ' is-active' : ''}`}
            style={{ '--notif-accent': THEME }}
            onClick={() => selectTab(TAB_ALL)}
          >
            All Notifications
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === TAB_PREFERENCES}
            className={`notif-tabs__tab${activeTab === TAB_PREFERENCES ? ' is-active' : ''}`}
            style={{ '--notif-accent': THEME }}
            onClick={() => selectTab(TAB_PREFERENCES)}
          >
            Preferences
          </button>
        </div>

        {activeTab === TAB_PREFERENCES ? (
          <NotificationPreferences />
        ) : historyLoading && notifications.length === 0 ? (
          <div className="module-card">
            <AppLoader color={THEME} />
          </div>
        ) : notifications.length === 0 ? (
          <div className="notif-page__empty">No notifications yet.</div>
        ) : (
          <div className="notif-page__list">
            {notifications.map((item) => {
              const Icon = typeIcon(item.type);
              return (
                <article
                  key={item.identity}
                  className={`notif-page__card${!item.isRead ? ' is-unread' : ''}`}
                  style={{ '--notif-accent': THEME }}
                  onClick={() => openNotification(item)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') openNotification(item);
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <span className="notif-page__card-icon" style={{ color: THEME }}>
                    <Icon className="w-5 h-5" />
                  </span>
                  <div className="notif-page__card-body">
                    <h3>{item.title}</h3>
                    {item.message ? <p>{item.message}</p> : null}
                    <div className="notif-page__meta">
                      <span>{notificationTypeLabel(item.type)}</span>
                      <span>{formatNotificationTime(item.createdDateTime)}</span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
